import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Fab,
  FormControlLabel,
  Checkbox,
  Menu,
  MenuItem,
  Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Send, KeyboardArrowDown, Image as ImageIcon, Close, AlternateEmail, Settings } from '@mui/icons-material';
import { useAuth } from 'AuthContext';
import useClubContext from 'contexts/Club';
import useFeedContext from 'contexts/Feed';
import { usePullToRefresh } from 'hooks/usePullToRefresh';
import PullToRefreshIndicator from 'UI/PullToRefreshIndicator';
import PostCard from 'components/PostCard';
import MentionInput from 'components/MentionInput';
import { uploadPostImages } from 'services/storage';
import { encodeMentions } from 'utils/mentionHelpers';
import { Keyboard } from '@capacitor/keyboard';
import { getPlatform, isNativeApp } from 'utils/platformHelpers';
import { composerNewlineOnEnter, handleComposerEnterKey } from 'utils/composerEnter';
import { triggerHaptic } from 'utils/haptics';

const FeedSection = () => {
  const { user } = useAuth();
  const { currentClub } = useClubContext();
  const {
    posts,
    loading,
    loadingMore,
    error,
    createPost,
    createReply,
    updatePost,
    fetchPosts,
    hasMore,
    loadMorePosts,
    showActivity,
    setShowActivity,
    replyingTo,
    clearReplyingTo,
    editingPost,
    clearEditingPost,
  } = useFeedContext();
  const [newPostText, setNewPostText] = useState('');
  const [isSpoiler, setIsSpoiler] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // { file, preview }
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [mentions, setMentions] = useState([]); // Track mentions in the post
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const justLoadedMoreRef = useRef(false); // Track if we just loaded older posts
  const previousFirstPostIdRef = useRef(null); // Track first post ID to detect new posts
  const previousEditingIdRef = useRef(null);
  const composerDraftRef = useRef(null);
  const composerStateRef = useRef({
    text: '',
    mentions: [],
    isSpoiler: false,
    selectedFiles: [],
  });
  const MAX_IMAGES = 5;

  const pullToRefresh = usePullToRefresh({
    ref: scrollContainerRef,
    onRefresh: fetchPosts,
    direction: 'bottom',
  });
  const MAX_IMAGE_SIZE_MB = 5;
  const isEditing = Boolean(editingPost?.id);
  const hasContent = isEditing
    ? newPostText.trim().length > 0
    : newPostText.trim().length > 0 || selectedFiles.length > 0;

  useEffect(() => {
    composerStateRef.current = {
      text: newPostText,
      mentions,
      isSpoiler,
      selectedFiles,
    };
  }, [newPostText, mentions, isSpoiler, selectedFiles]);

  useEffect(() => {
    return () => {
      selectedFiles.forEach(({ preview }) => URL.revokeObjectURL(preview));
    };
  }, [selectedFiles]);

  useEffect(() => {
    if (!replyingTo?.id || isEditing) return undefined;
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(focusTimer);
  }, [replyingTo?.id, isEditing]);

  useEffect(() => {
    const editId = editingPost?.id ?? null;
    const prevId = previousEditingIdRef.current;

    if (editId && editId !== prevId) {
      if (!prevId && !composerDraftRef.current) {
        composerDraftRef.current = {
          text: composerStateRef.current.text,
          mentions: composerStateRef.current.mentions,
          isSpoiler: composerStateRef.current.isSpoiler,
        };
      }
      setNewPostText(editingPost.text || '');
      setMentions(editingPost.mentions || []);
      setIsSpoiler(!!editingPost.isSpoiler);
      setUploadError(null);
      const focusTimer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      previousEditingIdRef.current = editId;
      return () => clearTimeout(focusTimer);
    }

    if (!editId && prevId) {
      const draft = composerDraftRef.current;
      composerDraftRef.current = null;
      if (draft) {
        setNewPostText(draft.text || '');
        setMentions(draft.mentions || []);
        setIsSpoiler(!!draft.isSpoiler);
      } else {
        setNewPostText('');
        setMentions([]);
        setIsSpoiler(false);
      }
      setUploadError(null);
    }

    previousEditingIdRef.current = editId;
  }, [editingPost]);

  const scrollToBottom = useCallback((options = {}) => {
    const behavior = options.behavior ?? 'smooth';
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // iOS native: after keyboard opens and layout reflows, keep latest messages above the composer
  useEffect(() => {
    if (!isNativeApp() || getPlatform() !== 'ios') return undefined;

    const showPromise = Keyboard.addListener('keyboardDidShow', () => {
      scrollToBottom({ behavior: 'smooth' });
    });

    return () => {
      showPromise.then((handle) => handle.remove());
    };
  }, [scrollToBottom]);

  // Scroll to bottom when new posts arrive (but not when loading more at top)
  useEffect(() => {
    if (posts.length === 0) {
      previousFirstPostIdRef.current = null;
      return;
    }

    const currentFirstPostId = posts[0]?.id;

    // Only auto-scroll if:
    // 1. Not loading initial posts
    // 2. Not currently loading more
    // 3. Didn't just load older posts (flag prevents auto-scroll)
    // 4. First post changed (new posts added at beginning) OR initial load
    if (
      !loading &&
      !loadingMore &&
      !justLoadedMoreRef.current &&
      (previousFirstPostIdRef.current === null || currentFirstPostId !== previousFirstPostIdRef.current)
    ) {
      scrollToBottom({ behavior: 'auto' });
    }

    // Only track first post id when the list is mounted (!loading); otherwise we
    // consume the id while the sentinel is still out of the DOM and skip scroll on load.
    if (!loading) {
      previousFirstPostIdRef.current = currentFirstPostId;
    }

    if (justLoadedMoreRef.current) {
      const timer = setTimeout(() => {
        justLoadedMoreRef.current = false;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [posts, loading, loadingMore, scrollToBottom]);

  // Detect scroll to top and load more posts
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !hasMore) return;

    let isLoading = false; // Prevent multiple simultaneous loads
    let scrollTimeout = null;

    const handleScroll = () => {
      // Debounce scroll events
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        // Check if scrolled near the top (within 200px) and not already loading
        if (scrollContainer.scrollTop < 200 && !isLoading && !loadingMore) {
          isLoading = true;
          justLoadedMoreRef.current = true; // Mark that we're loading older posts
          const scrollHeightBefore = scrollContainer.scrollHeight;
          const scrollTopBefore = scrollContainer.scrollTop;
          
          loadMorePosts().then(() => {
            // After loading, preserve scroll position
            // New content is added at the end of array (older posts), which appear at top after reversing
            // So we need to adjust scroll position to account for the new content height
            // Use multiple requestAnimationFrame calls to ensure DOM has fully updated
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const scrollHeightAfter = scrollContainer.scrollHeight;
                const heightDifference = scrollHeightAfter - scrollHeightBefore;
                // Adjust scroll position to keep the same content visible
                // Since older posts are added at the end of array (top after reversing),
                // we add the height difference to maintain position
                if (heightDifference > 0) {
                  scrollContainer.scrollTop = scrollTopBefore + heightDifference;
                }
                isLoading = false;
              });
            });
          }).catch(() => {
            isLoading = false;
            justLoadedMoreRef.current = false;
          });
        }
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [hasMore, loadingMore, loadMorePosts]);

  // Detect scroll position for scroll-to-bottom FAB
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 100;
      setShowScrollToBottom(!isNearBottom);
    };

    // Check initial position
    handleScroll();

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [posts]); // Re-check when posts change

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - selectedFiles.length;
    if (remainingSlots <= 0) {
      setUploadError(`You can attach up to ${MAX_IMAGES} images`);
      return;
    }

    const validFiles = files
      .filter((file) => file.type?.startsWith('image/'))
      .slice(0, remainingSlots);

    const oversized = validFiles.find((file) => file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024);
    if (oversized) {
      setUploadError(`Images must be under ${MAX_IMAGE_SIZE_MB}MB`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const filesWithPreview = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setSelectedFiles((prev) => [...prev, ...filesWithPreview]);
    setUploadError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev[index];
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleCreatePost = async () => {
    if (isSubmitting) return;
    if (!user || !currentClub) {
      setUploadError('You must be signed in to post');
      return;
    }

    if (editingPost?.id) {
      if (!newPostText.trim()) return;
      try {
        setIsSubmitting(true);
        setUploadError(null);
        const textWithMentions = encodeMentions(newPostText.trim(), mentions);
        await updatePost(editingPost.id, {
          text: textWithMentions,
          isSpoiler,
        });
        clearEditingPost();
        inputRef.current?.focus();
      } catch (err) {
        console.error('Error updating post:', err);
        setUploadError(err.message || 'Failed to update post');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!newPostText.trim() && selectedFiles.length === 0) return;

    try {
      setIsSubmitting(true);
      setUploadError(null);
      let imageUrls = [];

      if (selectedFiles.length > 0) {
        setUploadingImages(true);
        imageUrls = await uploadPostImages(
          currentClub?.id,
          user?.uid,
          selectedFiles.map(({ file }) => file),
          { maxCount: MAX_IMAGES, maxSizeMb: MAX_IMAGE_SIZE_MB },
        );
      }

      // Encode mentions in the text
      const textWithMentions = encodeMentions(newPostText.trim(), mentions);
      const payload = {
        text: textWithMentions,
        isSpoiler,
        images: imageUrls,
      };

      if (replyingTo?.id) {
        await createReply(replyingTo.id, payload);
        clearReplyingTo();
      } else {
        await createPost(payload);
      }

      setNewPostText('');
      setIsSpoiler(false);
      setMentions([]);
      selectedFiles.forEach(({ preview }) => URL.revokeObjectURL(preview));
      setSelectedFiles([]);
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error creating post:', err);
      setUploadError(err.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
      setUploadingImages(false);
    }
  };

  const handleKeyDown = (e) => {
    handleComposerEnterKey(e, {
      // Mobile software keyboards have no Shift+Enter; Enter is a new line.
      // Desktop: Enter sends, Shift+Enter inserts a new line.
      newlineOnEnter: composerNewlineOnEnter(),
      canSend: hasContent && !isSubmitting,
      onSend: handleCreatePost,
    });
  };

  const handleMentionsChange = useCallback((newMentions) => {
    setMentions(newMentions);
  }, []);

  const handleInsertMention = () => {
    if (!inputRef.current) return;
    
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const textBefore = newPostText.slice(0, start);
    const textAfter = newPostText.slice(end);
    
    // Insert @ at cursor position
    const newText = textBefore + '@' + textAfter;
    setNewPostText(newText);
    
    // Focus and set cursor after @ (MentionInput syncs suggestions on value change)
    setTimeout(() => {
      if (input) {
        const newCursorPos = start + 1;
        input.setSelectionRange(newCursorPos, newCursorPos);
        input.focus();
      }
    }, 0);
  };

  const handleOpenSettings = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setSettingsAnchorEl(null);
  };

  const handleManualRefresh = async () => {
    if (isManualRefreshing) return;
    try {
      setIsManualRefreshing(true);
      if (getPlatform() === 'ios') triggerHaptic('light');
      await fetchPosts();
    } catch (err) {
      console.error('Error refreshing feed:', err);
    } finally {
      setIsManualRefreshing(false);
      handleCloseSettings();
    }
  };

  const postsContent = useMemo(() => {
    return (
      <>
        {/* Loading More Indicator (at top) */}
        {loadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, mt: 'auto' }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        {/* Empty State */}
        {!loading && posts.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center', mt: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        )}

        {/* Posts List - Reversed order, newest at bottom */}
        {!loading && posts.length > 0 && (
          <Box sx={{ mt: 'auto' }}>
            {[...posts].reverse().map((post, index, reversedPosts) => {
              const prevPost = reversedPosts[index - 1];
              const currentAuthorId = post.authorId || post.author?.uid;
              const prevAuthorId = prevPost?.authorId || prevPost?.author?.uid;
              const currentTimestamp = post?.created_at || post?.createdAt;
              const prevTimestamp = prevPost?.created_at || prevPost?.createdAt;
              const isDayBreak = (() => {
                if (!prevTimestamp || !currentTimestamp) return false;
                const currentDate = new Date(currentTimestamp);
                const previousDate = new Date(prevTimestamp);
                if (Number.isNaN(currentDate.getTime()) || Number.isNaN(previousDate.getTime())) return false;
                const diffMs = Math.abs(currentDate.getTime() - previousDate.getTime());
                const oneDayMs = 24 * 60 * 60 * 1000;
                return diffMs > oneDayMs;
              })();
              const isFirstInGroup = index === 0 || prevAuthorId !== currentAuthorId || isDayBreak;
              return (
                <PostCard key={post.id} post={post} isFirstInGroup={isFirstInGroup} />
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </>
    );
  }, [error, loading, loadingMore, posts]);

  return (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        backgroundColor: 'background.default',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Messages Feed - Scrollable */}
          <Box
            ref={scrollContainerRef}
            sx={{
              height: '100%',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorY: 'auto',
              px: 2,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {postsContent}
            <PullToRefreshIndicator
              direction="bottom"
              pullProgress={pullToRefresh.pullProgress}
              pullDistance={pullToRefresh.pullDistance}
              isRefreshing={pullToRefresh.isRefreshing}
            />
          </Box>

          {/* Scroll-to-bottom FAB */}
          {showScrollToBottom && (
            <Fab
              size="small"
              color="primary"
              onClick={scrollToBottom}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                zIndex: 11,
                transition: 'opacity 0.2s, transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
              aria-label="Scroll to bottom"
            >
              <KeyboardArrowDown />
            </Fab>
          )}
        </Box>

        {/* Input Area */}
        <Paper
        elevation={0}
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFileChange}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {editingPost && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                backgroundColor: 'action.hover',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    lineHeight: 1.3,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    Editing message
                  </Box>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                    mt: 0.15,
                  }}
                >
                  {editingPost.isSpoiler
                    ? 'Spoiler content'
                    : (editingPost.previewText?.trim() || (editingPost.hasImages ? 'Photo' : 'Message'))}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={clearEditingPost}
                aria-label="Cancel edit"
                sx={{
                  mt: -0.25,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
          {!editingPost && replyingTo && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                backgroundColor: 'action.hover',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    lineHeight: 1.3,
                  }}
                >
                  Replying to{' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {replyingTo.authorName}
                  </Box>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                    mt: 0.15,
                  }}
                >
                  {replyingTo.isSpoiler
                    ? 'Spoiler content'
                    : (replyingTo.text?.trim() || (replyingTo.hasImages ? 'Photo' : 'Message'))}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={clearReplyingTo}
                aria-label="Cancel reply"
                sx={{
                  mt: -0.25,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <IconButton
              color="primary"
              onClick={handleFileButtonClick}
              disabled={isSubmitting || isEditing}
              sx={{
                backgroundColor: 'action.selected',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <ImageIcon />
            </IconButton>
            <MentionInput
              inputRef={inputRef}
              value={newPostText}
              onChange={(e) => setNewPostText(e.target.value)}
              onMentionsChange={handleMentionsChange}
              onKeyDown={handleKeyDown}
              placeholder={
                isEditing
                  ? 'Edit message...'
                  : (replyingTo ? `Reply to ${replyingTo.authorName}...` : 'Message...')
              }
              multiline
              maxRows={4}
              disabled={isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  borderRadius: 3,
                },
              }}
            />
            <IconButton
              color="primary"
            onClick={handleCreatePost}
            disabled={!hasContent || isSubmitting}
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.action.selected
                      : theme.palette.action.disabledBackground,
                  color: 'action.disabled',
                },
              }}
            >
              {isSubmitting ? <CircularProgress size={20} /> : <Send />}
            </IconButton>
          </Box>
          {selectedFiles.length > 0 && !isEditing && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              {selectedFiles.map(({ preview, file }, index) => (
                <Box
                  key={preview}
                  sx={{
                    position: 'relative',
                    width: 88,
                    height: 88,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                  }}
                >
                  <Box
                    component="img"
                    src={preview}
                    alt={file.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: (theme) =>
                        alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.6 : 0.45),
                      color: 'common.white',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.75 : 0.6),
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {uploadError && (
            <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
              {uploadError}
            </Typography>
          )}
          {uploadingImages && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Uploading images...
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSpoiler}
                    onChange={(e) => setIsSpoiler(e.target.checked)}
                    size="small"
                  />
                }
                label="Mark as spoiler"
                sx={{ ml: 0.5 }}
              />
              <IconButton
                size="small"
                onClick={handleInsertMention}
                disabled={isSubmitting}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
                title="Mention someone"
              >
                <AlternateEmail fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              onClick={handleOpenSettings}
              disabled={isSubmitting}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
              title="Feed settings"
            >
              <Settings fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={settingsAnchorEl}
              open={Boolean(settingsAnchorEl)}
              onClose={handleCloseSettings}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <MenuItem
                onClick={handleManualRefresh}
                disabled={isManualRefreshing}
                sx={{ gap: 1 }}
              >
                {isManualRefreshing ? <CircularProgress size={16} /> : null}
                Refresh feed
              </MenuItem>
              <MenuItem disableRipple sx={{ cursor: 'default' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showActivity}
                      onChange={(e) => {
                        if (getPlatform() === 'ios') triggerHaptic('light');
                        setShowActivity(e.target.checked);
                      }}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Show activity posts"
                  sx={{ m: 0 }}
                />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default FeedSection;
